import React, { useEffect, useReducer } from 'react';
import { ListItemLayout, ListLayout, Pager, Searchfield, SkillLevel, colourString, useMatchScreenWidth, scoreLevelRAGColour } from '@keplerco/core';
import * as store from './your-career-skills.store';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../overmind';
import { FetchType, OrganizationLevelType, FetchStatus } from '../../../enums';
import styles from './your-career-skills.module.css';
import classNames from 'classnames';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { SearchSkillsRequest, SkillScoresSearchParams } from '../../../models/overmind/search-params';
import { extractHighestOrganizationLevel } from '../../../library/helpers/permissions/extract-highest-organization-level';
import { EntitiesCardWidget } from '../../../components/cards/entities-card.widget';
import { SkillScoresRequest, SkillScoresSkillResponse } from '../../../models/overmind/analytics';
import { ResponseGroup } from '../../../enums/analytics';
import { CompanyRoleListItemResponse } from '../../../models/overmind/company-entity';

export default function YourCareerSkillsWidget({ role }: { role: CompanyRoleListItemResponse }) {
  const actions = useAppActions();
  const { user, companyVariables, permissions, fetchState } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const [skillScores, setSkillScores] = React.useState<SkillScoresSkillResponse[]>([]);

  const isMobile = useMatchScreenWidth('mobile');

  const organizationLevel = React.useMemo(
    () => (PagePath.yourCareerRole === PagePath.yourCareerRole ? OrganizationLevelType.Learner : user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.roleSkillManagement?.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.roleSkillManagement]
  );

  useEffect(() => {
    const request: SearchSkillsRequest = {
      search: undefined,
      sortAscending: true,
      pageSize: 4,
      page: 1,
      companySlug: companyVariables.slug,
      searchGlobally: false,
      roleSlugs: !!role.slug ? [role.slug] : [],
    };
    dispatch({ type: store.YourCareerSkillsActionTypes.SetRequest, payload: request });
  }, [role, companyVariables.slug]);

  useEffect(() => {
    async function getData() {
      const searchParams: SkillScoresSearchParams = {
        startDate: undefined,
        endDate: undefined,
        organizationLevel,
        companySlug: companyVariables.slug,
        departmentSlug: undefined,
        learnerSlug: user?.learnerSlug,
        teamSlug: undefined,
        skillSlug: undefined,
        skillSubTypeSlug: undefined,
        includeLearnerCount: false,
        scoreComparison: companyVariables.scoreComparison,
      };
      const request: SkillScoresRequest = {
        responseGroup: ResponseGroup.Skill,
        searchParams: searchParams,
      };

      const response = await actions.analyticsGetSkillScores(request);
      const skills: SkillScoresSkillResponse[] = [];
      if (!!response?.length) {
        response[0].skillCategories.forEach(skillCategory => skillCategory.skills.forEach(skill => skills.push(skill)));
      }
      setSkillScores(skills);
    }

    getData();
  }, [companyVariables.slug, user?.learnerSlug]);

  async function updateData() {
    if (!state.request) return actions.stopLoader(PagePath.yourCareerRole);
    const response = PagePath.yourCareerRole !== PagePath.yourCareerRole ? await actions.getCompanySkills(state.request) : await actions.getUserCompanySkills(state.request);
    dispatch({ type: store.YourCareerSkillsActionTypes.SetData, payload: response });
    actions.stopLoader(PagePath.yourCareerRole);
  }

  useEffect(() => {
    async function getData() {
      if (!state.request) return;
      actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.PageFetching });
      const response = await actions.getUserCompanySkills(state.request);
      if (!!response?.skills?.length) dispatch({ type: store.YourCareerSkillsActionTypes.SetData, payload: response });
      actions.stopLoader(PagePath.yourCareerRole);
    }

    !state.data ? getData() : updateData();
  }, [state.request]);

  return (
    <EntitiesCardWidget title="Skills" description="Skills assigned to the role" showSkillLevelHelp>
      {!!state.data && (
        <Searchfield
          label="Search skills"
          loading={fetchState[PagePath.yourCareerRole].status === FetchStatus.Active && fetchState[PagePath.yourCareerRole].type === FetchType.Custom}
          onInput={search => {
            if (!state.request) return;
            actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.Custom });
            dispatch({ type: store.YourCareerSkillsActionTypes.SetRequest, payload: { ...state.request, search, page: 1 } });
          }}
          responsive
        />
      )}

      <div style={{ minHeight: 370, marginTop: 15, marginBottom: !!state.data && state.data.totalPages > 1 ? 15 : 0 }}>
        {!!state.data?.skills.length ? (
          <ListLayout>
            {state.data.skills.map(entity => {
              const skillScore = skillScores.find(skillScore => skillScore.slug === entity.slug);
              return (
                <ListItemLayout key={entity.slug}>
                  <div className={classNames('card', styles.skillCard)} style={{ gridTemplateColumns: `1fr ${PagePath.yourCareerRole === PagePath.yourCareerRole && !isMobile ? '140px' : ''} ${!isMobile ? '140px' : ''}` }}>
                    <div style={{ overflow: 'hidden' }}>
                      <h4 className={styles.skillCardText} style={{ color: colourString('lilac') }}>
                        {entity.name}
                      </h4>
                      <small className={styles.skillCardText}>{entity.description}</small>
                    </div>

                    {PagePath.yourCareerRole === PagePath.yourCareerRole && <SkillLevel level={skillScore?.score?.level ?? companyVariables.minLevel} minLevel={companyVariables.minLevel} maxLevel={companyVariables.maxLevel} noLevel={!skillScore?.score} title="Your level" />}

                    <div className={classNames({ invisible: entity.level === null, hidden: entity.level === null && isMobile })}>
                      <SkillLevel
                        level={entity.level ?? companyVariables.minLevel}
                        minLevel={companyVariables.minLevel}
                        maxLevel={companyVariables.maxLevel}
                        noLevel={false}
                        title="Expected proficiency"
                        dotColour={PagePath.yourCareerRole !== PagePath.yourCareerRole ? scoreLevelRAGColour(entity.level ?? companyVariables.minLevel, companyVariables.minLevel, companyVariables.maxLevel) : 'baby-blue'}
                      />
                    </div>
                  </div>
                </ListItemLayout>
              );
            })}
          </ListLayout>
        ) : (
          <EmptyState title="No skills found" />
        )}
      </div>

      {!!state.data && state.data?.totalPages > 1 && (
        <Pager
          activePageNumber={state.request?.page ?? 1}
          pageCount={state.data?.totalPages}
          onPageChange={page => {
            if (!state.request || page === state.request.page) return;
            actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.Custom });
            dispatch({ type: store.YourCareerSkillsActionTypes.SetRequest, payload: { ...state.request, page } });
          }}
        />
      )}
    </EntitiesCardWidget>
  );
}
